var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useAriaLive } from '@ally/use-aria-live';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { a11y, visual } from './strings';
export const testId = {
    visibleCounter: 'mui-visible-character-counter',
    visuallyHidden: 'mui-hidden-character-counter',
};
const StyledCharacterCounter = styled.div `
  ${({ align, theme: { colors, font } }) => `
    color: ${colors['slate-5']};
    && {
      width: 100%;
      font-size: ${font.body.xs.size};
      font-weight: bold;
      text-align: ${align};
      text-transform: uppercase;
    }
  `}
`;
const screenReaderAnnouncement = (charCount, maxLength, getAriaLiveCharCount) => {
    return getAriaLiveCharCount(maxLength - charCount, maxLength);
};
/**
 * Accessibility requirements:
 *
 * <=100 chars
 * at 90% of max polite "# out of # characters remaining"
 * at 100% of max assert "0 out of # characters remaining"
 *
 *  >100 chars
 * at 80% of max polite "# out of # characters remaining"
 * at 90% of max polite "# out of # characters remaining"
 * at 95% of max assert "# out of # characters remaining"
 * at 100% of max assert "0 out of # characters remaining"
 */
const CharacterCounter = (_a) => {
    var { id, charCount, maxLength, align = 'left', hideAppendedText, getAriaLiveCharCount = a11y.getAriaLiveCharCount, appendedText = visual.appendedText } = _a, others = __rest(_a, ["id", "charCount", "maxLength", "align", "hideAppendedText", "getAriaLiveCharCount", "appendedText"]);
    const { announcePolite, announceAssertive } = useAriaLive();
    const makePoliteAnnouncement = useCallback(() => {
        announcePolite(screenReaderAnnouncement(charCount, maxLength, getAriaLiveCharCount));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charCount, maxLength, announcePolite]);
    const makeAssertiveAnnouncement = useCallback(() => {
        announceAssertive(screenReaderAnnouncement(charCount, maxLength, getAriaLiveCharCount));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charCount, maxLength, announceAssertive]);
    const politeBreakpoints = useMemo(() => {
        return maxLength <= 100
            ? [Math.floor(maxLength * 0.9)]
            : [Math.floor(maxLength * 0.8), Math.floor(maxLength * 0.9)];
    }, [maxLength]);
    const assertiveBreakpoints = useMemo(() => {
        return maxLength <= 100
            ? [maxLength]
            : [Math.floor(maxLength * 0.95), maxLength];
    }, [maxLength]);
    useEffect(() => {
        if (politeBreakpoints.includes(charCount)) {
            makePoliteAnnouncement();
        }
        else if (assertiveBreakpoints.includes(charCount)) {
            makeAssertiveAnnouncement();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charCount, politeBreakpoints, assertiveBreakpoints]);
    return (React.createElement("div", null,
        React.createElement(StyledCharacterCounter, Object.assign({ "aria-hidden": "true", align: align, "data-testid": testId.visibleCounter }, others), `${charCount}/${maxLength}${hideAppendedText ? '' : ` ${appendedText}`}`),
        React.createElement(ScreenReaderOnly, { id: id, "data-testid": testId.visuallyHidden }, screenReaderAnnouncement(charCount, maxLength, getAriaLiveCharCount))));
};
export { CharacterCounter };
//# sourceMappingURL=CharacterCounter.js.map